import { useCallback } from 'react'

import { useDrop } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'

import { SUPPORTED_FORMATS } from '@/features/FileManager/fileManagerSlice'

type FilesItem = {
  files: File[]
  items: DataTransferItemList
}

function getFileTypes(item: FilesItem | null): string[] {
  if (!item) {
    return []
  }

  const { files, items } = item

  if (files && files.length > 0) {
    return files.map((f) => f.type)
  }

  if (items && items.length > 0) {
    return Array.from(items).map((i) => i.type)
  }

  return []
}

export function useDropFile(handleDropFiles) {
  const handleCanDrop = useCallback((files: File[]) => {
    const fileTypes = files.map((file) => file.type)
    // Can't drop if there are unsupported files in the selection
    return fileTypes.every((ft) => SUPPORTED_FORMATS.includes(ft))
  }, [])

  const [{ isOver, validFiles }, dropRef] = useDrop<{ files }, any, any>(() => {
    return {
      accept: NativeTypes.FILE,
      drop: ({ files }) => handleDropFiles(files),
      canDrop: ({ files }) => handleCanDrop(files),
      collect: (monitor) => {
        const item = monitor.getItem() as FilesItem | null
        const types = getFileTypes(item)

        const validFiles = types.every((it) => SUPPORTED_FORMATS.includes(it))

        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
          validFiles,
        }
      },
    }
  }, [handleDropFiles, handleCanDrop, getFileTypes])

  return {
    dropRef,
    isOver,
    validFiles,
  }
}
